import { gql as contentfulGql } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import { runContentfulExperimentOnServer } from '@/experimentation/server'
import { getContentfulClient } from '@/services/ApolloClient'
import {
  GetGiveawayContentQuery,
  GiveawayHero,
  GiveawayShareModal,
  GiveawayStep,
  Maybe,
  PageGiveaway,
} from '@/types/codegen-contentful'
import { getFirstCollectionItemAs } from '@/utils/contentful/collection-helpers'

const GiveawayCustomComponentFragment = contentfulGql/* GraphQL */ `
  fragment GiveawayCustomComponentFragment on GiveawayCustomComponent {
    __typename
    sys {
      id
    }
    customComponent
  }
`

const GrowthbookExperimentMappingsFragment = contentfulGql/* GraphQL */ `
  fragment GrowthbookExperimentMappingsFragment on GrowthbookExperimentMappings {
    __typename
    sys {
      id
    }
    rolloutVariationId
  }
`

export const PageGiveawayQuery = contentfulGql/* GraphQL */ `
  query getGiveawayContent($theatricalSlug: String!, $preview: Boolean!) {
    pageGiveawayCollection(limit: 1, where: { theatricalSlug: $theatricalSlug }, preview: $preview) {
      items {
        theatricalSlug
        logoUrl
        footerLogoUrl
        seoImageUrl
        trailerExperimentCollection {
          items {
            ...GrowthbookExperimentMappingsFragment
            ...GiveawayCustomComponentFragment
            ... on YoutubeTrailerId {
              __typename
              sys {
                id
              }
              youtubeId
            }
          }
        }
        heroExperimentCollection {
          items {
            ...GrowthbookExperimentMappingsFragment
            ...GiveawayCustomComponentFragment
            ... on GiveawayHero {
              __typename
              sys {
                id
              }
              backgroundVideoUrl
              desktopImageUrl
              mobileImageUrl
              giveawayYoutubeId
              title
            }
          }
        }
        stepsExperimentCollection {
          items {
            ...GrowthbookExperimentMappingsFragment
            ...GiveawayCustomComponentFragment
            ... on GiveawaySteps {
              __typename
              sys {
                id
              }
              stepsCollection {
                items {
                  title
                  description
                }
              }
            }
          }
        }
        partnerText
        partnerLogosCollection {
          items {
            imageUrl
            altText
            maxWidth
          }
        }
        shareModalExperimentCollection {
          items {
            ...GrowthbookExperimentMappingsFragment
            ...GiveawayCustomComponentFragment
            ... on GiveawayShareModal {
              __typename
              sys {
                id
              }
              shareText
              desktopImageUrl
              mobileImageUrl
            }
          }
        }
        tabsCollection(limit: 5) {
          items {
            ... on GiveawayDetailsTab {
              __typename
              sys {
                id
              }
              title
              videoThumbnail
              text {
                json
              }
              galleryImagesCollection(limit: 10) {
                items {
                  sys {
                    id
                  }
                  altText
                  imageUrl
                  maxWidth
                }
              }
            }
            ... on GiveawayPrizesTab {
              __typename
              sys {
                id
              }
              title
              text {
                json
              }
              prizesSectionsCollection(limit: 3) {
                items {
                  sys {
                    id
                  }
                  title
                  prizesCollection(limit: 20) {
                    items {
                      sys {
                        id
                      }
                      title
                      imageUrl
                      altText
                      value
                      url
                      sponsorName
                      representativePrizeShown
                    }
                  }
                }
              }
            }
            ... on GiveawayFaqTab {
              __typename
              sys {
                id
              }
              title
              faqsCollection(limit: 10) {
                items {
                  sys {
                    id
                  }
                  key
                  question
                  answer {
                    json
                  }
                }
              }
            }
          }
        }
        rules {
          json
        }
        rulesUrl
        theme {
          backgroundPrimaryDark
          backgroundPrimaryLight
          backgroundSubtleLight
          backgroundSubtlestLight
          primary
          primaryAccent
          primaryAccentDark
          primaryDark
          primarySubtle
          primarySubtleLight
          secondary
          secondaryAccent
          subtle
          textPrimaryDark
          textPrimaryLight
          textSubtleLight
        }
      }
    }
  }
  ${GiveawayCustomComponentFragment}
  ${GrowthbookExperimentMappingsFragment}
`

export const GET_GIVEAWAY_ASSET = contentfulGql/* GraphQL */ `
  query getGiveawayAsset($id: String!) {
    asset(id: $id) {
      sys {
        id
      }
      url
      title
      width
      height
      description
      contentType
    }
  }
`

export const GET_GIVEAWAY_YOUTUBE_TRAILER = contentfulGql/* GraphQL */ `
  query getGiveawayYoutubeTrailer($id: String!, $preview: Boolean!) {
    youtubeTrailerId(id: $id, preview: $preview) {
      __typename
      sys {
        id
      }
      youtubeId
    }
  }
`

export const getGiveawayContent = async (theatricalSlug: string, preview?: boolean): Promise<PageGiveaway | null> => {
  const client = getContentfulClient({ preview })
  const result = await client.query<GetGiveawayContentQuery>({
    query: PageGiveawayQuery,
    variables: { theatricalSlug, preview },
    errorPolicy: 'all',
  })

  return getFirstCollectionItemAs<PageGiveaway>(result?.data?.pageGiveawayCollection?.items)
}

export interface PageGiveawayContent extends PageGiveaway {
  hero?: GiveawayHero | null
  shareModal?: Maybe<GiveawayShareModal> | null
  steps?: Maybe<GiveawayStep>[] | null
  youtubeTrailerId?: string
}

export const resolvePageGiveawayContent = async (
  content: PageGiveaway | null,
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
): Promise<PageGiveawayContent | null> => {
  if (!content) return null

  const resolvedContent: PageGiveawayContent = {
    ...content,
  }
  // Resolve the youtube trailer id
  if (content?.trailerExperimentCollection?.items) {
    const trailerExperiment = await runContentfulExperimentOnServer(req, res, content.trailerExperimentCollection.items)
    if (trailerExperiment?.__typename === 'YoutubeTrailerId') {
      resolvedContent.youtubeTrailerId = trailerExperiment.youtubeId ?? ''
    }
    // TODO: handle giveaway custom component (we don't have any yet)
    // if (trailerExperiment?.__typename === 'GiveawayCustomComponent') {
  }

  // Resolve the hero
  if (content?.heroExperimentCollection?.items) {
    const heroExperiment = await runContentfulExperimentOnServer(req, res, content.heroExperimentCollection.items)
    if (heroExperiment?.__typename === 'GiveawayHero') {
      resolvedContent.hero = heroExperiment
    }
    // TODO: handle giveaway custom component
  }

  // Resolve the steps
  if (content?.stepsExperimentCollection?.items) {
    const stepsExperiment = await runContentfulExperimentOnServer(req, res, content.stepsExperimentCollection.items)
    if (stepsExperiment?.__typename === 'GiveawaySteps') {
      resolvedContent.steps = stepsExperiment.stepsCollection?.items
    }
    // TODO: handle giveaway custom component
  }

  // Resolve the share modal
  if (content?.shareModalExperimentCollection?.items) {
    const shareModalExperiment = await runContentfulExperimentOnServer(
      req,
      res,
      content.shareModalExperimentCollection.items,
    )
    if (shareModalExperiment?.__typename === 'GiveawayShareModal') {
      resolvedContent.shareModal = shareModalExperiment
    }
  }

  return resolvedContent
}
